import { useState } from "react";
import { toast } from "sonner";
import { ICON_NAMES } from "@/components/site/icons";
import { adminApi, errMsg, waLink } from "@/lib/api";
import { useAdmin } from "../AdminContext";
import { PageHead, Panel, Field, ImageField, TagsField, ListEditor, Toggle, Input } from "../fields";
import { Dashboard, Analytics } from "./Dashboard";

const Profile = () => (
  <div>
    <PageHead title="Doctor Profile" desc="Name, degrees, photo and the welcome message at the top of the website." />
    <Panel title="Photos">
      <ImageField path="profile.photo" label="Doctor photo" hint="A portrait photo works best." />
      <ImageField path="profile.logo" label="Clinic logo" hint="Shown in the header and as the browser tab icon." />
    </Panel>
    <Panel title="Name & degrees">
      <Field path="profile.name" label="Doctor name" />
      <Field path="profile.designation" label="Designation" />
      <Field path="profile.designation_local" label="Designation (local language, optional)" />
      <Field path="profile.degrees" label="Degrees" />
      <Field path="profile.clinic_name" label="Clinic name" />
    </Panel>
    <Panel title="Welcome message">
      <Field path="profile.welcome_title" label="Welcome title" />
      <Field path="profile.welcome_message" label="Welcome message" multiline />
    </Panel>
    <Panel title="About the doctor">
      <Field path="profile.bio" label="Biography" multiline rows={6} />
      <Field path="profile.extra_info" label="Extra information (optional)" multiline />
      <TagsField path="profile.specializations" label="Specializations" placeholder="e.g. Newborn Care" />
    </Panel>
  </div>
);

const Gallery = () => (
  <div>
    <PageHead title="Photos & Gallery" desc="Upload photos for the gallery section and change the decorative pictures." />
    <Panel title="Gallery photos">
      <ListEditor path="gallery" addLabel="Add photo" itemTitle={(x) => x.caption || "Photo"} newItem={{ image: "", caption: "" }}
        fields={[{ key: "image", label: "Photo", type: "image" }, { key: "caption", label: "Caption" }]} />
    </Panel>
    <Panel title="Decorative pictures">
      <ImageField path="settings.hero_decor_image" label="Picture next to doctor photo (teddy)" />
      <ImageField path="settings.about_decor_image" label="Picture in About section (kids)" />
    </Panel>
  </div>
);

const Experience = () => (
  <div>
    <PageHead title="Experience & Qualifications" desc="Shown in the About the Doctor section." />
    <Panel title="Qualifications">
      <Field path="settings.qualifications_label" label="Heading" />
      <ListEditor path="qualifications" addLabel="Add qualification" itemTitle={(x) => x.title} newItem={{ title: "", institution: "", year: "" }}
        fields={[{ key: "title", label: "Degree / qualification" }, { key: "institution", label: "Institution / details" }, { key: "year", label: "Year (optional)" }]} />
    </Panel>
    <Panel title="Professional experience">
      <Field path="settings.experience_label" label="Heading" />
      <ListEditor path="experience" addLabel="Add experience" itemTitle={(x) => x.role || x.place} newItem={{ role: "", place: "", period: "", description: "" }}
        fields={[{ key: "role", label: "Role" }, { key: "place", label: "Hospital / clinic" }, { key: "period", label: "Period (e.g. 2019 – Present)" }, { key: "description", label: "Description (optional)", type: "textarea" }]} />
    </Panel>
    <Panel title="Labels">
      <Field path="settings.specialization_label" label="Specialization heading" />
    </Panel>
  </div>
);

const Awards = () => (
  <div>
    <PageHead title="Awards & Achievements" desc="Recognitions, certificates and conferences." />
    <Panel>
      <ListEditor path="awards" addLabel="Add award" itemTitle={(x) => x.title} newItem={{ title: "", year: "", description: "", image: "" }}
        fields={[{ key: "image", label: "Photo / certificate", type: "image" }, { key: "title", label: "Title" }, { key: "year", label: "Year" }, { key: "description", label: "Description", type: "textarea" }]} />
    </Panel>
  </div>
);

const Services = () => (
  <div>
    <PageHead title="Services Available" desc="The list of services shown on the website." />
    <Panel><Field path="services.intro" label="Intro text" multiline rows={2} /></Panel>
    <Panel title="Services">
      <ListEditor path="services.items" addLabel="Add service" itemTitle={(x) => x.title} newItem={{ icon: "stethoscope", title: "", description: "" }}
        fields={[{ key: "icon", label: "Icon", type: "select", options: ICON_NAMES }, { key: "title", label: "Service name" }, { key: "description", label: "Short description", type: "textarea", rows: 2 }]} />
    </Panel>
  </div>
);

const Reviews = () => (
  <div>
    <PageHead title="Parent Reviews" desc="Your rating and the reviews shown on the website." />
    <Panel title="Overall rating">
      <div className="grid gap-4 sm:grid-cols-3">
        <Field path="reviews.rating" label="Rating (e.g. 5.0)" />
        <Field path="reviews.count" label="Number of reviews" />
        <Field path="reviews.source" label="Source (e.g. Google)" />
      </div>
      <Field path="reviews.link" label="Link to all reviews (optional)" placeholder="https://..." />
    </Panel>
    <Panel title="Reviews">
      <ListEditor path="reviews.items" addLabel="Add review" itemTitle={(x) => x.name} newItem={{ name: "", rating: "5", date: "", text: "" }}
        fields={[{ key: "name", label: "Parent name" }, { key: "rating", label: "Stars", type: "select", options: ["5", "4", "3", "2", "1"] }, { key: "date", label: "Date (e.g. Sep 2026)" }, { key: "text", label: "Review", type: "textarea" }]} />
    </Panel>
  </div>
);

const Library = () => (
  <div>
    <PageHead title="Child Health Library" desc="Write health notes and articles for parents. They appear on the website after you publish." />
    <Panel title="Articles">
      <ListEditor path="articles" addLabel="Add article" emptyText="No articles yet. Click Add article to write your first one." itemTitle={(x) => x.title}
        newItem={{ title: "", category: "", date: "", image: "", summary: "", content: "", published: true }}
        fields={[
          { key: "published", label: "Show on website", type: "switch" },
          { key: "title", label: "Title" }, { key: "category", label: "Category (e.g. Vaccination)" }, { key: "date", label: "Date (e.g. Oct 2026)" },
          { key: "image", label: "Cover photo", type: "image" },
          { key: "summary", label: "Short summary", type: "textarea", rows: 2 },
          { key: "content", label: "Full article", type: "textarea", rows: 12, hint: "Leave an empty line between paragraphs." },
        ]} />
    </Panel>
    <Panel title="When there are no articles"><Field path="settings.library_empty_text" label="Message" /></Panel>
  </div>
);

const Content = () => (
  <div>
    <PageHead title="Website Content" desc="Section headings, menu names, order and visibility." />
    <Panel title="Sections" desc="Use the arrows to change the order on the website. Turn off a section to hide it.">
      <ListEditor path="sections" allowAdd={false} itemTitle={(x) => `${x.title}${x.visible === false ? " (hidden)" : ""}`}
        fields={[{ key: "visible", label: "Show this section", type: "switch" }, { key: "title", label: "Heading" }, { key: "subtitle", label: "Small text above heading" }, { key: "nav_label", label: "Name in top menu" }]} />
    </Panel>
    <Panel title="Header & buttons">
      <Field path="settings.home_nav_label" label="'Home' menu name" />
      <Field path="settings.header_button_text" label="Header button text" />
      <Field path="settings.call_button_prefix" label="Call button text (before number)" />
    </Panel>
    <Panel title="Search engines & footer">
      <Field path="settings.site_title" label="Browser tab title" />
      <Field path="settings.meta_description" label="Description for Google" multiline rows={3} />
      <Field path="settings.footer_text" label="Extra footer text (optional)" multiline rows={2} />
      <Field path="settings.ga_id" label="Google Analytics ID (optional)" placeholder="G-XXXXXXX" />
    </Panel>
  </div>
);

const Clinic = () => (
  <div>
    <PageHead title="Clinic Information" desc="Address, phone, timings and map." />
    <Panel title="Address & contact">
      <Field path="clinic.name" label="Building / centre name" />
      <Field path="clinic.address" label="Address" multiline rows={2} />
      <Field path="clinic.phone" label="Phone number" />
      <Field path="clinic.email" label="Email (optional)" />
    </Panel>
    <Panel title="Consultation timings">
      <ListEditor path="clinic.timings" addLabel="Add timing" itemTitle={(x) => [x.days, x.time].filter(Boolean).join(": ")} newItem={{ days: "", time: "" }}
        fields={[{ key: "days", label: "Days / label (e.g. Mon – Sat)" }, { key: "time", label: "Time (e.g. 4:30 PM – 7:30 PM)" }]} />
      <Field path="clinic.note" label="Note under timings (optional)" />
    </Panel>
    <Panel title="Map">
      <Toggle path="clinic.show_map" label="Show Google map" />
      <Field path="clinic.map_query" label="Map search text" hint="Type the clinic name and address exactly as found on Google Maps." multiline rows={2} />
    </Panel>
    <Panel title="Labels">
      <div className="grid gap-4 sm:grid-cols-3">
        <Field path="clinic.labels.address" label="Address label" />
        <Field path="clinic.labels.phone" label="Phone label" />
        <Field path="clinic.labels.timings" label="Timings label" />
      </div>
    </Panel>
  </div>
);

const WhatsApp = () => {
  const { content } = useAdmin();
  return (
    <div>
      <PageHead title="WhatsApp Settings" desc="Where appointment messages go and what they say." />
      <Panel>
        <Field path="whatsapp.number" label="WhatsApp number (with country code)" hint="Example: 918787724913 (91 for India, then the number)." />
        <Field path="whatsapp.message" label="Pre-filled message" multiline rows={3} />
        <Field path="whatsapp.button_text" label="Button text" />
        <a href={waLink(content?.whatsapp)} target="_blank" rel="noreferrer" className="inline-flex rounded-full bg-[#25D366] px-5 py-2.5 text-sm font-semibold text-white" data-testid="whatsapp-test-link">Test this WhatsApp link</a>
      </Panel>
    </div>
  );
};

const Account = () => {
  const [cur, setCur] = useState("");
  const [nw, setNw] = useState("");
  const { discard, busy } = useAdmin();
  const submit = async () => {
    try { await adminApi.post("/auth/change-password", { current_password: cur, new_password: nw }); toast.success("Password changed"); setCur(""); setNw(""); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div>
      <PageHead title="Account & Password" />
      <Panel title="Change password">
        <Input label="Current password" type="password" value={cur} onChange={setCur} testId="current-password-input" />
        <Input label="New password" type="password" value={nw} onChange={setNw} hint="At least 6 characters." testId="new-password-input" />
        <button onClick={submit} className="rounded-full bg-blue-900 px-5 py-2.5 text-sm font-semibold text-white" data-testid="change-password-btn">Change password</button>
      </Panel>
      <Panel title="Undo all unpublished changes" desc="Throws away the draft and goes back to what is live on the website now.">
        <button onClick={() => window.confirm("Discard all unpublished changes?") && discard()} disabled={!!busy} className="rounded-full border border-red-200 px-5 py-2.5 text-sm font-semibold text-red-600 hover:bg-red-50" data-testid="discard-draft-btn">Discard draft</button>
      </Panel>
    </div>
  );
};

export const PAGES = {
  dashboard: Dashboard, profile: Profile, gallery: Gallery, experience: Experience, awards: Awards, services: Services,
  reviews: Reviews, library: Library, content: Content, clinic: Clinic, whatsapp: WhatsApp, analytics: Analytics, account: Account,
};
